import React from "react";
import styled from "styled-components";
import typo from "@/src/styles/typography";
import { Button } from "@/src/components/atom/Button";
import Icon from "@/src/components/atom/Icon";
import ChevronDownUpIcon from "@/src/assets/icons/icon-chevron-up-down.svg";
import ChevronUpDownIcon from "@/src/assets/icons/icon-chevron-down-up.svg";
import CheckmarkIcon from "@/src/assets/icons/icon-checkmark.svg";
import {
  useContractProgress,
  type ButtonActionType,
  type ContractData,
  type ContractSignature,
  type SignatureHistory,
} from "@/src/hooks/useContractProgress";

interface ContractProgressBarProps {
  userRole: "EXPORTER" | "IMPORTER";
  contractData?: ContractData | null;
  contractSignature?: ContractSignature | null;
  signatureHistories?: SignatureHistory[] | null;
  onAction?: (actionType: ButtonActionType) => void;
  rejectReason?: string;
  contractId?: number;
}

const ContractProgressBar: React.FC<ContractProgressBarProps> = ({
  userRole,
  contractData,
  contractSignature,
  signatureHistories,
  onAction,
  rejectReason,
  contractId,
}) => {
  const {
    stages,
    currentStepInfo,
    isExpanded,
    toggleExpanded,
    handleButtonClick,
    getStepColor,
    getBackgroundColor,
    getBorderColor,
  } = useContractProgress({
    userRole,
    contractData,
    contractSignature,
    signatureHistories,
    contractId,
    onAction,
    rejectReason,
  });

  // 데이터가 없으면 렌더링하지 않음
  if (!contractData || !stages || stages.length === 0) {
    return null;
  }

  const currentStep = currentStepInfo.step;
  const currentStepNumber = currentStepInfo.index + 1;

  // 접힌 모드: 현재 단계만 표시
  if (!isExpanded && currentStep) {
    return (
      <StepContainer
        $backgroundColor={getBackgroundColor(currentStep.status)}
        $borderColor={getBorderColor(currentStep.status)}
        $isExpanded={false}
      >
        <StepContent>
          {currentStep.status === "completed" ||
          currentStep.status === "final" ? (
            <CompletedCircle isFinal={currentStep.status === "final"}>
              <CheckIconWrapper isFinal={currentStep.status === "final"}>
                <Icon iconSrc={CheckmarkIcon} iconSize={16} />
              </CheckIconWrapper>
            </CompletedCircle>
          ) : (
            <StepCircle color={getStepColor(currentStep.status)}>
              <StepNumber
                color={getStepColor(currentStep.status)}
                $status={currentStep.status}
              >
                {currentStepNumber}
              </StepNumber>
            </StepCircle>
          )}

          <StepTextContainer>
            <StepTitle $status={currentStep.status}>{currentStep.title}</StepTitle>
            {currentStep.message && (
              <StepMessage $status={currentStep.status}>{currentStep.message}</StepMessage>
            )}
          </StepTextContainer>
        </StepContent>

        <ButtonContainer>
          {currentStep.showButton && currentStep.buttonText && (
            <ActionButton
              buttonColor={currentStep.status === "error" ? "red" : "blue"}
              buttonGrade={
                currentStep.status === "error" ? "tertiary" : "primary"
              }
              buttonSize={32}
              onClick={() => handleButtonClick(currentStep)}
            >
              {currentStep.buttonText}
            </ActionButton>
          )}

          {!currentStep.showButton && currentStep.buttonText && (
            <StatusBadge color={getStepColor(currentStep.status)}>
              {currentStep.buttonText}
            </StatusBadge>
          )}
        </ButtonContainer>

        <ToggleButton onClick={toggleExpanded}>
          <Icon iconSrc={ChevronDownUpIcon} iconSize={24} />
        </ToggleButton>
      </StepContainer>
    );
  }

  // 펼쳐진 모드: 전체 히스토리 표시
  return (
    <ExpandedContainer
      $backgroundColor={getBackgroundColor(currentStep?.status || "pending")}
      $borderColor={getBorderColor(currentStep?.status || "pending")}
    >
      <ConnectionLineVertical />

      <ExpandedHeader>
        <CloseToggleButton onClick={toggleExpanded}>
          <Icon iconSrc={ChevronUpDownIcon} iconSize={24} />
        </CloseToggleButton>
      </ExpandedHeader>

      {stages.map((step, index) => (
        <StepRow key={index}>
          <StepRowContent>
            {step.status === "completed" || step.status === "final" ? (
              <CompletedCircle isFinal={step.status === "final"}>
                <CheckIconWrapper isFinal={step.status === "final"}>
                  <Icon iconSrc={CheckmarkIcon} iconSize={16} />
                </CheckIconWrapper>
              </CompletedCircle>
            ) : (
              <StepCircle color={getStepColor(step.status)}>
                <StepNumber
                  color={getStepColor(step.status)}
                  $status={step.status}
                >
                  {index + 1}
                </StepNumber>
              </StepCircle>
            )}

            <StepTextContainer>
              <StepTitle $status={step.status}>{step.title}</StepTitle>
              {step.message && step.status !== "pending" && (
                <StepMessage $status={step.status}>{step.message}</StepMessage>
              )}
            </StepTextContainer>
          </StepRowContent>

          {/* 버튼/배지 영역 - StepRowContent 밖에 위치 */}
          <ButtonContainer>
            {/* 현재 단계인 경우 버튼 또는 상태 배지 표시 */}
            {index === currentStepInfo.index && step.buttonText && (
              <>
                {step.showButton ? (
                  <ActionButton
                    buttonColor={step.status === "error" ? "red" : "blue"}
                    buttonGrade={
                      step.status === "error" ? "tertiary" : "primary"
                    }
                    buttonSize={32}
                    onClick={() => handleButtonClick(step)}
                  >
                    {step.buttonText}
                  </ActionButton>
                ) : (
                  <StatusBadge color={getStepColor(step.status)}>
                    {step.buttonText}
                  </StatusBadge>
                )}
              </>
            )}

            {/* 현재 단계가 아닌 경우, 상태 뱃지만 표시 */}
            {index !== currentStepInfo.index &&
              !step.showButton &&
              step.buttonText &&
              step.status !== "pending" && (
                <StatusBadge color={getStepColor(step.status)}>
                  {step.buttonText}
                </StatusBadge>
              )}
          </ButtonContainer>

          {/* 토글 버튼 공간 확보용 */}
          <ToggleButtonSpace />
        </StepRow>
      ))}
    </ExpandedContainer>
  );
};

export default ContractProgressBar;

// Styled Components
const StepContainer = styled.div<{
  $backgroundColor: string;
  $borderColor: string;
  $isExpanded: boolean;
}>`
  width: 100%;
  height: 100%;
  padding: 16px;
  background: ${(props) => props.$backgroundColor};
  overflow: hidden;
  border-radius: 12px;
  outline: 1px ${(props) => props.$borderColor} solid;
  outline-offset: -1px;
  justify-content: flex-start;
  align-items: center;
  gap: 0; /* gap 제거 */
  display: inline-flex;
`;

const StepContent = styled.div`
  flex: 1 1 0;
  justify-content: flex-start;
  align-items: center;
  gap: 12px;
  display: flex;
  min-height: 32px; /* 버튼/배지 높이에 맞춰 최소 높이 고정 */
`;

const StepCircle = styled.div<{ color: string }>`
  border-radius: 64px;
  justify-content: center;
  align-items: center;
  display: flex;
  position: relative;
  z-index: 1;
  width: 22px;
  height: 22px;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const StepNumber = styled.div<{ color: string; $status: string }>`
  width: 100%;
  height: 100%;
  background: ${(props) =>
    props.$status === "pending" ? "white" : props.color};
  border-radius: 64px;
  outline: 1px
    ${(props) => {
      if (props.$status === "pending") return "#CFD6D9";
      if (props.color === "#166DD7") return "#1761C2";
      if (props.color === "#15BD66") return "#0A9A32";
      if (props.color === "#CC0000") return "#B30000";
      return props.color;
    }}
    solid;
  outline-offset: -1px;

  display: flex;
  justify-content: center;
  align-items: center;

  color: ${(props) => (props.$status === "pending" ? "#6E777C" : "white")};
  font-size: 13px;
  font-family: "Pretendard";
  font-weight: 700;
  line-height: 18px;
  text-align: center;
`;

const CompletedCircle = styled.div<{ isFinal?: boolean }>`
  width: 22px;
  height: 22px;
  background: #15bd66;
  border-radius: 64px;
  outline: 1px #0a9a32 solid;
  outline-offset: -1px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 1;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const CheckIconWrapper = styled.div<{ isFinal?: boolean }>`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 16px;
    height: 16px;
    path {
      fill: white;
    }
  }
`;

const StepTextContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  min-height: 24px; /* 텍스트 높이 고정 */
`;

const StepTitle = styled.div<{ $status?: string }>`
  color: ${(props) => props.$status === "pending" ? "#6E777C" : "#222729"};
  font-size: 16px;
  font-family: "Pretendard";
  font-weight: 700;
  line-height: 24px;
  flex-shrink: 0;
`;

const StepMessage = styled.div<{ $status?: string }>`
  color: ${(props) => props.$status === "pending" ? "#6E777C" : "#5e696e"};
  font-size: 15px;
  font-family: "Pretendard";
  font-weight: 400;
  line-height: 20px;
`;

const ActionButton = styled(Button)<{
  buttonGrade?: string;
  buttonColor?: string;
}>`
  height: 32px;
  padding: 6px 12px;
  box-shadow: 0px 1px 2px rgba(5, 29, 57, 0.1);
  border-radius: 8px;
  border: none;
  position: relative;
  z-index: 2;

  font-size: 15px;
  font-family: "Pretendard";
  font-weight: 500;
  line-height: 20px;
  flex-shrink: 0;

  /* 반려 사유 보기 버튼(빨간색 tertiary) 스타일 */
  ${(props) =>
    props.buttonColor === "red" &&
    props.buttonGrade === "tertiary" &&
    `
    background: white;
    color: #CC0000;
    border: none;
    box-shadow: 0px 0px 0px 0.5px #E7EDF0, 0px 1px 2px 0px rgba(5, 29, 57, 0.10);
    outline: none;
    
    &:hover {
      background: white;
      color: #CC0000;
      border: none;
      box-shadow: 0px 0px 0px 0.5px rgba(160, 164, 171, 0.20), 0px 2px 4px 0px rgba(5, 29, 57, 0.20);
      transform: translateY(-2px);
    }
    
    &:focus {
      outline: none;
      border: none;
    }
  `}

  ${(props) =>
    (!props.buttonGrade || props.buttonGrade === "primary") &&
    `
    background: #1C3D61;
    color: white;
  `}
`;

const StatusBadge = styled.div<{ color: string }>`
  padding: 4px 14px;
  background: white;
  border-radius: 24px;
  color: ${(props) => props.color};
  font-size: 15px;
  font-weight: 500;
  line-height: 20px;
  ${typo.b9m}
  flex-shrink: 0;
  height: 32px; /* 버튼과 동일한 높이 설정 */
  display: flex;
  align-items: center;
`;

const ExpandedContainer = styled.div<{
  $backgroundColor: string;
  $borderColor: string;
}>`
  width: 100%;
  padding: 16px;
  position: relative;
  background: ${(props) => props.$backgroundColor};
  overflow: hidden;
  border-radius: 12px;
  outline: 1px ${(props) => props.$borderColor} solid;
  outline-offset: -1px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 0; /* gap 제거 */
  display: inline-flex;
`;

const ExpandedHeader = styled.div`
  width: calc(100% - 32px); /* padding 고려 */
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 1;
  pointer-events: none; /* 클릭 이벤트가 아래 요소로 전달되도록 */
  
  & > button {
    pointer-events: auto; /* 토글 버튼은 클릭 가능하도록 */
  }
`;

const ConnectionLineVertical = styled.div`
  width: 2px;
  height: calc(100% - 60px);
  left: 26px;
  top: 35px;
  position: absolute;
  background: #cfd6d9;
  z-index: 0;
`;

const StepRow = styled.div`
  width: 100%;
  justify-content: space-between;
  align-items: center;
  display: flex;

  &:first-child {
    margin-top: 48px; /* ExpandedHeader 공간 확보 */
  }

  &:not(:last-child) {
    margin-bottom: 24px; /* 각 row 사이의 간격 */
  }
`;

const StepRowContent = styled.div`
  flex: 1;
  justify-content: flex-start;
  align-items: center;
  gap: 12px;
  display: flex;
  min-height: 32px; /* 버튼/배지 높이에 맞춰 최소 높이 고정 */
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  justify-content: flex-end;
  width: 120px; /* 고정 너비로 collapse 모드와 동일한 위치 */
  min-width: 120px;
  position: relative;
  z-index: 2;
`;

const ToggleButtonSpace = styled.div`
  width: 52px; /* ToggleButton width(28px) + margin-left(24px) */
  flex-shrink: 0;
`;

const ToggleButton = styled.button`
  height: 28px;
  width: 28px;
  justify-content: center;
  align-items: center;
  display: flex;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  margin-left: 24px; /* ButtonContainer와의 고정 간격 */
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const CloseToggleButton = styled(ToggleButton)`
  position: static;
  margin: 0; /* 상속받은 margin 제거 */
  margin-left: 24px; /* ButtonContainer와의 간격 */
`;
